import { FC, useEffect, useState } from "react";
import {
  Autosuggest,
  SpaceBetween,
  TokenGroup,
} from "@cloudscape-design/components";
import {
  createItemWithId,
  deleteItem,
  fetchItems,
  fetchItemsWithFilter,
} from "../api/generic";
import InventoryItem from "../models/Inventory";
import { prefixCollection } from "../utils/prefix";
import { slugify } from "../utils/string";
import { useDebounce } from "react-use";

type Category = {
  id: string; // Slug
  label: string; // Display name
  createdAt: Date;
};

type Props = {
  tenant: string;
  values: string[]; // slugs
  onChange: (categories: string[]) => void;
};

const MultiCategorySuggest: FC<Props> = ({ tenant, values, onChange }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [input, setInput] = useState("");

  const [debouncedInput, setDebouncedInput] = useState(input);
  useDebounce(() => setDebouncedInput(input), 500, [input]);

  const catCol = prefixCollection("category", tenant);
  const inventoryCol = prefixCollection("inventory", tenant);

  useEffect(() => {
    const load = async () => {
      const all = await fetchItems<Category>(catCol);
      setCategories(all);
    };
    load();
  }, [catCol]);

  const addCategory = async (label: string) => {
    const slug = slugify(label);
    if (values.includes(slug)) return;

    const existing = categories.find((c) => c.id === slug);
    if (!existing) {
      const newCat = { id: slug, label: label.trim(), createdAt: new Date() };
      await createItemWithId(catCol, slug, newCat);
      setCategories((prev) => [...prev, newCat]);
    }

    onChange([...values, slug]);
    setInput("");
  };

  const removeCategory = async (slug: string) => {
    const updated = values.filter((cat) => cat !== slug);
    onChange(updated);

    const stillUsed = await fetchItemsWithFilter<InventoryItem>(inventoryCol, [
      { field: "categories", operator: "array-contains", value: slug },
    ]);
    if (stillUsed.length === 0) {
      await deleteItem(catCol, slug);
      setCategories((prev) => prev.filter((c) => c.id !== slug));
    }
  };

  const displayLabels = values.map(
    (slug) => categories.find((c) => c.id === slug)?.label || slug
  );

  const filteredOptions = categories
    .filter((cat) =>
      cat.label.toLowerCase().includes(debouncedInput.toLowerCase())
    )
    .map((cat) => ({ label: cat.label }));

  return (
    <SpaceBetween size="xs">
      <Autosuggest
        value={input}
        onChange={({ detail }) => setInput(detail.value)}
        onKeyDown={(e) => {
          const key = e.detail.key;
          if (key === "Enter" && input.trim()) {
            addCategory(input);
            e.preventDefault();
          }
        }}
        onSelect={({ detail }) => {
          const selected = detail.selectedOption?.label;
          const typed = detail.value?.trim();

          if (selected) {
            addCategory(selected);
          } else if (typed) {
            addCategory(typed); // ✅ This handles "Create category" click
          }
        }}
        enteredTextLabel={(val) => `Create category "${val}"`}
        placeholder="Start typing to add a category"
        options={filteredOptions}
      />
      <TokenGroup
        onDismiss={({ detail }) => removeCategory(values[detail.itemIndex])}
        items={displayLabels.map((label) => ({ label }))}
      />
    </SpaceBetween>
  );
};

export default MultiCategorySuggest;
